import '../../styles/ActivityLogForm.css';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../auth/authConfig';
import { Form, FormGroup, Label, Input, Button, Alert, Row, Col } from 'reactstrap';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import env from 'react-dotenv';
import ActivityLogDataTable from '../DataTables/ActivityLogDataTable';

export const ActivityLogForm = () => {
    const [days, setDays] = useState('30');
    const [useDateRange, setUseDateRange] = useState(false);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [eventName, setEventName] = useState('');
    const [search, setSearch] = useState('');
    const [events, setEvents] = useState(null);
    const [eventCount, setEventCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [lastRefreshed, setLastRefreshed] = useState(null);
    const isFetchingRef = useRef(false);
    const { instance, accounts } = useMsal();

    const fetchLogs = useCallback(async (params) => {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;
        setLoading(true);
        setMessage(null);

        try {
            const tokenResponse = await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0],
            });
            let response;
            try {
                response = await fetch(`${env.API_ACTIVITY_LOG_ENDPOINT}?${params}`, {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${tokenResponse.accessToken}`,
                        'Content-Type': 'application/json',
                    },
                });
            } catch (e) {
                console.error(e);
                setMessage({ type: 'danger', text: 'Error communicating with server' });
                return;
            }
            if (response.status === 200) {
                const data = await response.json();
                if (!data.eventsFound || data.eventCount === 0) {
                    setMessage({ type: 'warning', text: 'No events found for the selected filters.' });
                    setEvents(null);
                } else {
                    setEvents(data.events);
                    setEventCount(data.eventCount);
                    setLastRefreshed(new Date());
                }
            } else if (response.status === 403) {
                setMessage({ type: 'danger', text: 'You do not have authorization to view activity logs.' });
            } else if (response.status === 500) {
                setMessage({ type: 'danger', text: 'An error occurred.' });
            }
        } finally {
            isFetchingRef.current = false;
            setLoading(false);
        }
    }, [instance, accounts]);

    const defaultParams = () => new URLSearchParams({ days: '30' });

    useEffect(() => {
        fetchLogs(defaultParams());
    }, []);

    const handleDaysChange = (val) => {
        setDays(val);
    };

    const handleEventNameChange = (val) => {
        setEventName(val);
    };

    const handleSearchChange = (val) => {
        setSearch(val);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (useDateRange) {
            if (!dateFrom || !dateTo) {
                setMessage({ type: 'warning', text: 'Please select both From and To dates.' });
                return;
            }
            if (new Date(dateFrom) > new Date(dateTo)) {
                setMessage({ type: 'warning', text: 'From date cannot be after To date.' });
                return;
            }
        }

        const params = new URLSearchParams();
        if (useDateRange) {
            params.append('dateFrom', dateFrom);
            params.append('dateTo', dateTo);
        } else {
            params.append('days', days);
        }
        if (eventName.trim()) params.append('eventName', eventName.trim());
        if (search.trim()) params.append('search', search.trim());

        await fetchLogs(params);
    };

    return (
        <div>
            <h3 style={{ marginLeft: '1em', marginTop: '.25em', color: 'darkslategray' }}>
                Activity Log
            </h3>
            <div className='ActivityLogForm'>
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={12} className="mb-2 d-flex align-items-center justify-content-between">
                            <FormGroup check className="mb-0">
                                <Label check>
                                    <Input type="checkbox" checked={useDateRange} onChange={e => setUseDateRange(e.target.checked)} disabled={loading} />{' '}
                                    Select date range
                                </Label>
                            </FormGroup>
                            {lastRefreshed && (
                                <small className="text-muted">Last updated {lastRefreshed.toLocaleTimeString()}</small>
                            )}
                        </Col>

                        {!useDateRange ? (
                            <Col md={2}>
                                <FormGroup>
                                    <Label for="days">Days</Label>
                                    <Input type="select" id="days" value={days} onChange={e => handleDaysChange(e.target.value)} disabled={loading}>
                                        <option value="7">Last 7 days</option>
                                        <option value="14">Last 14 days</option>
                                        <option value="30">Last 30 days</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                        ) : (
                            <>
                                <Col md={2}>
                                    <FormGroup>
                                        <Label for="dateFrom">From</Label>
                                        <Input type="date" id="dateFrom" value={dateFrom} onChange={e => setDateFrom(e.target.value)} disabled={loading} />
                                    </FormGroup>
                                </Col>
                                <Col md={2}>
                                    <FormGroup>
                                        <Label for="dateTo">To</Label>
                                        <Input type="date" id="dateTo" value={dateTo} onChange={e => setDateTo(e.target.value)} disabled={loading} />
                                    </FormGroup>
                                </Col>
                            </>
                        )}

                        <Col md={3}>
                            <FormGroup>
                                <Label for="eventName">Event Name</Label>
                                <Input type="text" id="eventName" placeholder="search with event name..." value={eventName} onChange={e => handleEventNameChange(e.target.value)} disabled={loading} />
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="search">Search</Label>
                                <Input type="text" id="search" placeholder="Search via text or email..." value={search} onChange={e => handleSearchChange(e.target.value)} disabled={loading} />
                            </FormGroup>
                        </Col>
                        <Col md={2} className="d-flex align-items-end">
                            <FormGroup>
                                <Button type="submit" className='button' disabled={loading} style={{ width: '105px' }}>
                                    {loading ? <Spinner animation='border' size='sm' /> : <><FontAwesomeIcon icon={faSearch} /> Search</>}
                                </Button>
                            </FormGroup>
                        </Col>
                    </Row>
                </Form>

                {message && (
                    <div className="mt-4">
                        <Alert color={message.type}>{message.text}</Alert>
                    </div>
                )}

                {events && (
                    <div className="mt-4">
                        <Label>{eventCount} event{eventCount !== 1 ? 's' : ''} found</Label>
                        <ActivityLogDataTable events={events} />
                    </div>
                )}
            </div>
        </div>
    );
};
